import get from 'lodash/get';

import FilterOperatorEnum from '@constants/enums/filter/operator';
import FilterTypeEnum from '@constants/enums/filter/type';

import { toDatetime, toIso8601 } from '@utils/date';

import clock from '@plugins/dayjs';
import i18n from '@plugins/i18n';

/**
 * Returns a list of operators that don't require criteria.
 * @returns Array
 */
export const getNoCriteriaOperators = () => {
    return Object.values(FilterOperatorEnum)
        .filter(o => !o.requiresCriteria)
        .map(o => o.value);
};

/**
 * Returns the FilterOperator object for the given key
 * @param {String} key
 * @returns {Object}
 */
export const getFilterOperator = key => {
    if (!(typeof key === 'string')) {
        return null;
    }

    key = key.toUpperCase();

    return get(FilterOperatorEnum, [key, 'value'], null);
};

/**
 * Return a FieldType object
 * @param {String} key
 * @returns {Object} FieldType
 */
export const getFieldType = key => {
    if (!(typeof key === 'string')) {
        return null;
    }

    key = key.toUpperCase();

    if (!(key in FilterTypeEnum)) {
        return null;
    }

    return FilterTypeEnum[`${key}`];
};

export const filterMatchesItem = (item, key, filter) => {
    let match = true;

    if (Array.isArray(filter)) {
        match = filter.every(value => this.filterMatchesItem(item, key, value));
    } else if (key in item) {
        const hasOperator = filter.includes(':');

        let operator = FilterOperatorEnum.IS.value;
        let criteria = null;

        if (hasOperator) {
            [operator, criteria] = filter.match(/([^:]+):(.*)/).slice(1);
        } else {
            criteria = filter;
        }

        if (operator === FilterOperatorEnum.IN.value) {
            criteria = criteria.replace('[', '').replace(']', '').replace(' ', '').split(',');
        }

        const value = item[`${key}`];

        if (operator === FilterOperatorEnum.CONTAINS.value) {
            match = typeof value === 'string' && value.includes(criteria);
        } else if (operator === FilterOperatorEnum.NOT_CONTAINS.value) {
            match = typeof value === 'string' && !value.includes(criteria);
        } else if (operator === FilterOperatorEnum.STARTS_WITH.value) {
            match = typeof value === 'string' && value.startsWith(criteria);
        } else if (operator === FilterOperatorEnum.ENDS_WITH.value) {
            match = typeof value === 'string' && value.endsWith(criteria);
        } else if (operator === FilterOperatorEnum.IS.value) {
            match = value === criteria;
            if (!match && clock(value).isValid() && clock(criteria).isValid()) {
                match = clock(value).startOf('day').isSame(clock(criteria).startOf('day'));
            }
        } else if (operator === FilterOperatorEnum.IS_NOT.value) {
            match = value !== criteria;
        } else if (operator === FilterOperatorEnum.EMPTY.value) {
            match =
                value === null ||
                value === undefined ||
                ((typeof value === 'string' || Array.isArray(value)) && !value.length) ||
                (typeof value === 'object' && !Object.keys(value).length);
        } else if (operator === FilterOperatorEnum.NOT_EMPTY.value) {
            match =
                value !== null &&
                value !== undefined &&
                (typeof value === 'string' || Array.isArray(value)) &&
                value.length > 0 &&
                typeof value === 'object' &&
                Object.keys(value).length > 0;
        } else if (operator === FilterOperatorEnum.LESS_THAN_OR_IS.value) {
            match = typeof value === 'number' && value <= criteria;
        } else if (operator === FilterOperatorEnum.MORE_THAN_OR_IS.value) {
            match = typeof value === 'number' && value >= criteria;
        } else if (operator === FilterOperatorEnum.IS_BEFORE.value) {
            match = typeof value === 'string' && clock(toIso8601(value)).isBefore(clock(toIso8601(criteria))) === true;
        } else if (operator === FilterOperatorEnum.IS_AFTER.value) {
            match = typeof value === 'string' && clock(toIso8601(value)).isAfter(clock(toIso8601(criteria))) === true;
        } else if (operator === FilterOperatorEnum.IN.value) {
            match = Array.isArray(criteria) && criteria.includes(value);
        }

        if (!match) {
            console.warn('Match not found:', { key, value, filter });
        }
    } else {
        match = false;
    }

    return match;
};

/**
 * Converts a filter object into a human-readable text string.
 * @param {Object} filter - The filter object containing operator and criteria.
 * @param {Object} option - The option object describing the filterable field.
 * @returns {String|null} The human-readable filter string, or null if invalid.
 */

export const filterToText = (filter, option = null) => {
    if (!option) {
        return null;
    }

    const operator = option.operators.find(op => op.value === filter.operator);

    if (!operator) {
        return null;
    }

    const requiresCriteria = !getNoCriteriaOperators().includes(filter.operator);

    let written = [];

    written.push(`${option.label} `);

    if (operator.value === 'in') {
        written.push(i18n.t('is_either').toLowerCase());
    } else if (operator.value === 'not_in') {
        written.push(i18n.t('is_not').toLowerCase());
    } else {
        written.push(`${operator.text.toLowerCase()}`);
    }

    if (requiresCriteria) {
        let criteria = filter.criteria;

        if (Array.isArray(criteria)) {
            const criteriaString = criteria.map(c => `"${c}"`).join(', ');
            written.push(criteriaString.replace(/,([^,]*)$/, ' ' + i18n.t('or').toLowerCase() + '$1'));
        } else if (requiresCriteria) {
            if (filter.data && filter.data.name) {
                criteria = filter.data.name;
            }
            let criteriaText = '';

            if (option.format === 'money') {
                criteriaText = `${criteria !== null ? criteria.amount : ''}`;
            } else if (option.format === 'boolean') {
                criteriaText = `${criteria ? 'true' : 'false'}`;
            } else if (option.format === 'date') {
                criteriaText = toDatetime(criteria, 'DD/MM/YYYY');
            } else if (option.format === 'datetime') {
                criteriaText = toDatetime(criteria, 'DD/MM/YYYY');
            } else if (option.format === 'enum' && option.enumA11yKey) {
                criteriaText = `${criteria !== null ? i18n.t(option.enumA11yKey + '.' + criteria) : ''}`;
            } else {
                criteriaText = `"${criteria !== null ? criteria : ''}"`;
            }

            if (option.prependText) {
                criteriaText = `${option.prependText} ${criteriaText}`;
            }

            if (option.appendText) {
                criteriaText += ` ${option.appendText}`;
            }

            written.push(criteriaText);
        }
    }

    return written.join(' ');
};
